import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceStoreActions } from '@era/stores';
import { OdataService } from '@methylium/odata';
import { ListLazyComponent } from '@ml-ag-grid-lib';
import { Actions, ofType } from '@ngrx/effects';
import { PanelComponent, PanelHeaderComponent, TitleComponent } from '@rocksoft/cdk';
import { DevicesService } from '@rocksoft/mdm-sdk-v2';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { DevicesOverviewDatasource } from './devices-overview-datasource';
import { MdmDevicesAgGridCellRendererComponent } from './mdm-devices-ag-grid-cell-renderer/mdm-devices-ag-grid-cell-renderer.component';
import { MdmDevicesAgGridHeaderComponent } from './mdm-devices-ag-grid-header/mdm-devices-ag-grid-header.component';

@Component({
  selector: 'ml-mdm-devices-overview',
  templateUrl: './devices-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PanelComponent, PanelHeaderComponent, TitleComponent, ListLazyComponent],
})
export class DevicesOverviewComponent {
  private readonly api = inject(DevicesService);
  private readonly odataService = inject(OdataService);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private gridApi?: GridApi;

  public dataSource: DevicesOverviewDatasource;

  public columnDefs: ColDef[] = [
    {
      field: 'serialNumber',
      sortable: true,
      pinned: 'left',
      suppressMovable: true,
      resizable: false,
      headerComponent: MdmDevicesAgGridHeaderComponent,
      cellRenderer: MdmDevicesAgGridCellRendererComponent,
      wrapText: true,
      cellClass: 'ml-custom-cell',
      headerClass: 'ml-custom-header-cell',
      filter: 'agTextColumnFilter',
      filterParams: {
        maxNumConditions: 1,
      },
    },
  ];

  constructor() {
    this.dataSource = new DevicesOverviewDatasource(this.api, this.odataService);

    this.actions$
      .pipe(ofType(DeviceStoreActions.DeviceSignalRHubActions.mdmDeviceChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ device }) => {
        const updateResult = this.dataSource.updateDevice(device);

        if (updateResult && !updateResult.shouldRefreshGrid) {
          this.gridApi?.getRowNode(updateResult.device.udid)?.setData(updateResult.device);
          return;
        }

        this.refreshGrid();
      });

    this.actions$
      .pipe(ofType(DeviceStoreActions.DeviceSignalRHubActions.mdmDeviceRemoved), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshGrid();
      });
  }

  private refreshGrid(): void {
    this.dataSource.refresh();
    this.gridApi?.refreshInfiniteCache();
  }

  public onGridReady(gridReadyEvent: GridReadyEvent): void {
    this.gridApi = gridReadyEvent.api;
    gridReadyEvent.api.hideOverlay();
  }
}
